import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllCheckInStaff,
    addCheckInStaffs,
    regenerateInviteCode
} from "../../../../../../app/store/slices/event/team/team";
import { errorToast, successToastTopRight } from "../../../../../../utils/toasts/toasts";

export default function useEventCreatorScanningLink() {
    const dispatch = useDispatch();

    const { eventData } = useSelector((state) => state.event);
    const { user } = useSelector((state) => state.auth);
    const { checkInStaffList } = useSelector(state => state.team);

    const [loading, setLoading] = useState(false);


    /**
     * Effect to fetch all check-in staff members when the component mounts
     */
    useEffect(() => {
        if (eventData?.eventId) {
            dispatch(getAllCheckInStaff({ eventId: eventData?.eventId, type: 'staff' })).unwrap();
        }
    }, [eventData?.eventId, dispatch]);


    /**
     * Check if the event creator already has a scanning link generated
     * Filters the check-in staff list for records matching the current user with access
     * 
     * @returns {Array}
     */
    const creatorLinks = useMemo(() => {
        if (!checkInStaffList?.data) return [];

        return checkInStaffList.data.filter(record =>
            record.user_id === user?.id && record.access === true
        );
    }, [checkInStaffList, user?.id]);


    // Get the latest creator link record if available
    const latestCreatorRecord = creatorLinks.length > 0 ? creatorLinks[creatorLinks.length - 1] : null;


    // Extract the event creator link or set to null if not available
    const eventCreatorLink = latestCreatorRecord?.link ?? null;


    /**
     * @async function: to generate a new scanning link for the event creator
     * Dispatches the addCheckInStaffs action with the current user's details
     * Handles loading state and displays success/error toasts
     * 
     * @returns {void}
     */
    async function handleGenerateLink() {
        try {

            if (!eventData || !user) {
                errorToast("Event data or user data is missing!");
                return;
            }

            setLoading(true);

            const payload = {
                type: 'staff',
                invites: [
                    {
                        name: user.name,
                        email: user.email,
                        phone: user.phone
                    }
                ]
            };

            await dispatch(addCheckInStaffs({ eventId: eventData?.eventId, payload })).unwrap();

            successToastTopRight("Scanning link generated successfully!");

        } catch (error) {
            errorToast("Failed to generate scanning link");
        } finally {
            setLoading(false);
        }
    }


    /**
     * @async function: to copy the scanning link to clipboard
     * Handles success/error toasts
     * 
     * @returns {void}
     */
    const handleCopyLink = useCallback(async () => {
        if (!eventCreatorLink) return;

        try {
            await navigator.clipboard.writeText(eventCreatorLink);
            successToastTopRight("Link copied!");
        } catch {
            errorToast("Failed to copy link");
        }
    }, [eventCreatorLink]);


    /**
     * @async function: to regenerate the scanning link for the event creator
     * Dispatches the regenerateInviteCode action with the latest creator record ID
     * Handles loading state and displays success/error toasts
     * 
     * @returns {void}
     */
    async function handleRegenerateLink() {
        try {
            setLoading(true);

            await dispatch(regenerateInviteCode({ recordId: latestCreatorRecord?.id })).unwrap();

            successToastTopRight("Invitation code regenerated successfully");
        } catch (error) {
            errorToast("Failed to regenerate invitation code");
        } finally {
            setLoading(false);
        }
    }

    return {
        loading,
        handleGenerateLink,
        handleCopyLink,
        handleRegenerateLink,
        eventCreatorLink
    }
}